import React from "react";
import {
  Box,
  Card,
  CardBody,
  Checkbox,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Tag,
  Text,
  Tooltip,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { ArrowCounterClockwise, Bell, CalendarBlank, DotsThreeVertical, Eye, Flame, Lightning, Lock, PencilSimple, Trash, TreeStructure, Truck, User } from "@phosphor-icons/react";
import { QUESTIONNAIRE_STATUSES } from "../../../../../utils";
import { reminderSentLabel } from "../../../../../utils/reminderLabel";
import { useAuth } from "../../../../../context";

// Exported so SendReminderModal.jsx can render identical scope chips/title in its header and in the
// subject-line default, rather than re-deriving the same values a second time.
export const SCOPE_COLORS = {
  scope1: { base: "#25C469", light: "#E8FAF0", text: "#0B5E30", icon: Flame, label: "Scope 1" },
  scope2: { base: "#5EB5F3", light: "#E8F4FD", text: "#1A5276", icon: Lightning, label: "Scope 2" },
  scope3: { base: "#9A34F3", light: "#F3E8FE", text: "#5B1D99", icon: Truck, label: "Scope 3" },
};

// Exported for the same reason as SCOPE_COLORS/getTitle above — SendReminderModal.jsx's preview needs
// the same "In progress"/"Not started"/"Finalized" label the card itself shows.
export const STATUS_CONFIG = {
  [QUESTIONNAIRE_STATUSES.NEW]: { progress: 15, color: "#FC8181", label: "No data" },
  [QUESTIONNAIRE_STATUSES.IN_PROGRESS]: { progress: 50, color: "#FBD38D", label: "In Progress" },
  [QUESTIONNAIRE_STATUSES.FINALIZED]: { progress: 100, color: "#B8E086", label: "Finalized" },
};

export const parseScopeInfo = (task) => {
  const tags = [];
  const configs = task.configurations || {};

  if (configs.directEmissionsShouldBeFilled) {
    tags.push({ ...SCOPE_COLORS.scope1 });
  }
  if (configs.indirectEmissionsShouldBeFilled) {
    tags.push({ ...SCOPE_COLORS.scope2 });
  }

  const scope3Cats = [];
  if (configs.purchasedGoodsAndServicesShouldBeFilled) scope3Cats.push("1");
  if (configs.capitalGoodsShouldBeFilled) scope3Cats.push("2");
  if (configs.fuelAndEnergyRelatedActivitiesShouldBeFilled) scope3Cats.push("3");
  if (configs.upstreamTransportationAndDistributionShouldBeFilled) scope3Cats.push("4");
  if (configs.wasteGeneratedInOperationsShouldBeFilled) scope3Cats.push("5");
  if (configs.businessTravelShouldBeFilled) scope3Cats.push("6");
  if (configs.employeeCommutingShouldBeFilled) scope3Cats.push("7");
  if (configs.downstreamTransportationAndDistributionShouldBeFilled) scope3Cats.push("9");
  if (configs.useOfSoldProductsShouldBeFilled) scope3Cats.push("11");
  if (configs.treatmentOfSoldProductsShouldBeFilled) scope3Cats.push("12");

  if (scope3Cats.length > 0) {
    tags.push({ ...SCOPE_COLORS.scope3, categories: scope3Cats });
  }

  return tags;
};

export const getTitle = (task) => {
  if (task.description) return task.description;
  const parts = [];
  if (task.year) parts.push(task.year);
  if (task.quarter && task.quarter !== "ALL") parts.push(task.quarter);
  const specificDims = (task.dimensions || []).filter((d) => d.value !== "All");
  if (specificDims.length > 0) {
    parts.push(specificDims[specificDims.length - 1].value);
  }
  return parts.join(" · ") || "";
};

const TaskCard = ({ task, onEdit, onDelete, onView, onEditData, onUnfinalise, onSendReminder, isJustReminded = false, t, isAdmin = true, isSelected = false, onToggleSelect, selectionMode = false }) => {
  const canEdit = task.originalStatus !== QUESTIONNAIRE_STATUSES.FINALIZED;
  const scopeTags = parseScopeInfo(task);
  const title = getTitle(task);
  const statusConfig = STATUS_CONFIG[task.originalStatus] || STATUS_CONFIG[QUESTIONNAIRE_STATUSES.NEW];
  const selectable = isAdmin && !!onToggleSelect;
  // "Send reminder" needs a real person to email and a task that is still open — a finalised task has
  // nothing left to nudge anyone about, and a task with no assignee has no one to send it to.
  const canSendReminder = isAdmin && !!task.assigneeId && task.originalStatus !== QUESTIONNAIRE_STATUSES.FINALIZED;
  const { user } = useAuth();
  const reminderLabel = reminderSentLabel(task.lastReminder, user?.email);

  const handleCardClick = () => {
    if (selectionMode && selectable) {
      onToggleSelect(task.id);
      return;
    }
    if (canEdit) {
      onEditData(task.id);
    } else {
      onView(task.id);
    }
  };

  return (
    <Card
      w="100%"
      borderWidth={isSelected ? "2px" : "1px"}
      borderRadius="lg"
      borderColor={isSelected ? "lime" : "gray.200"}
      bg="white"
      boxShadow="sm"
      cursor="pointer"
      _hover={{ borderColor: "lime" }}
      transition="border-color 0.15s"
      onClick={handleCardClick}
    >
      {/* Header with scope pills + kebab */}
      <Flex
        justify="space-between"
        align="center"
        bg="gray.50"
        px={5}
        py={3}
        borderBottom="1px solid"
        borderColor="gray.100"
        borderTopRadius="lg"
        gap={3}
      >
        {selectable && selectionMode && (
          <Box onClick={(e) => e.stopPropagation()} flexShrink={0}>
            <Checkbox
              isChecked={isSelected}
              onChange={() => onToggleSelect(task.id)}
              colorScheme="limeShade"
              size="md"
              sx={{
                ".chakra-checkbox__control": {
                  borderRadius: "sm",
                  borderColor: "gray.400",
                  bg: "white",
                },
                ".chakra-checkbox__control[data-checked]": {
                  bg: "lime",
                  borderColor: "lime",
                  color: "pureBlack",
                },
              }}
            />
          </Box>
        )}
        <Wrap spacing={1.5} flex={1} minW={0}>
          {scopeTags.map((scope, idx) => (
            <WrapItem key={idx}>
              <Flex align="center" gap={0}>
                {/* Lock the label to a single line — using a plain Box so
                    nothing inside Chakra's Tag component can break it. */}
                <Box
                  display="inline-flex"
                  alignItems="center"
                  gap="4px"
                  bg={scope.light}
                  color={scope.text}
                  fontSize="11px"
                  fontWeight="600"
                  px="8px"
                  h="20px"
                  borderRadius="full"
                  borderRightRadius={scope.categories ? 0 : "full"}
                  flexShrink={0}
                  flexWrap="nowrap"
                  whiteSpace="nowrap"
                  width="max-content"
                  minWidth="max-content"
                >
                  <scope.icon size={12} weight="fill" style={{ flexShrink: 0 }} />
                  <Box as="span" whiteSpace="nowrap" minWidth="max-content">
                    {scope.label.replace(/ /g, " ")}
                  </Box>
                </Box>
                {scope.categories && (
                  <Box
                    display="inline-flex"
                    alignItems="center"
                    bg={scope.light}
                    color={scope.text}
                    fontSize="11px"
                    fontWeight="400"
                    px="8px"
                    minH="20px"
                    borderRadius="full"
                    borderLeftRadius={0}
                    opacity={0.8}
                    minW={0}
                    sx={{
                      whiteSpace: "normal",
                      wordBreak: "normal",
                      overflowWrap: "normal",
                    }}
                  >
                    · Cat {scope.categories.join(", ")}
                  </Box>
                )}
              </Flex>
            </WrapItem>
          ))}
        </Wrap>
          <Box onClick={(e) => e.stopPropagation()} flexShrink={0} ml={2}>
            <Menu isLazy>
              <Tooltip label={t("page.actionsTooltip")}>
                <MenuButton
                  as={IconButton}
                  aria-label="Actions"
                  icon={<DotsThreeVertical size={20} weight="bold" />}
                  variant="ghost"
                  size="sm"
                  borderRadius="full"
                />
              </Tooltip>
              <MenuList borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1} minW="180px">
                <MenuItem icon={<Eye size={16} />} onClick={() => onView(task.id)} fontSize="sm" color="gray.700">
                  {t("page.viewEmissionsData")}
                </MenuItem>
                <MenuItem icon={<PencilSimple size={16} />} onClick={() => onEditData(task.id)} isDisabled={task.originalStatus === QUESTIONNAIRE_STATUSES.FINALIZED} fontSize="sm" color="gray.700">
                  {t("page.editEmissionsData")}
                </MenuItem>
                {isAdmin && (
                  <MenuItem icon={<PencilSimple size={16} />} onClick={() => onEdit(task)} isDisabled={!task.updatable} fontSize="sm" color="gray.700">
                    {t("page.editTask")}
                  </MenuItem>
                )}
                {isAdmin && (
                  <MenuItem icon={<ArrowCounterClockwise size={16} />} onClick={() => onUnfinalise(task.id)} isDisabled={task.originalStatus !== QUESTIONNAIRE_STATUSES.FINALIZED} fontSize="sm" color="gray.700">
                    {t("page.unfinaliseGHGQuestionnaire")}
                  </MenuItem>
                )}
                {isAdmin && canSendReminder && (
                  <MenuItem icon={<Bell size={16} />} onClick={() => onSendReminder(task)} fontSize="sm" color="gray.700">
                    Send reminder
                  </MenuItem>
                )}
                {isAdmin && <MenuDivider />}
                {isAdmin && (
                  <Tooltip label={task.originalStatus === QUESTIONNAIRE_STATUSES.FINALIZED ? "Finalised tasks cannot be deleted — unfinalise first" : ""} placement="left">
                    <MenuItem icon={<Trash size={16} color="#E53E3E" />} onClick={() => onDelete(task.id)} isDisabled={task.originalStatus === QUESTIONNAIRE_STATUSES.FINALIZED} fontSize="sm" color="red.500">
                      {t("page.deleteTask")}
                    </MenuItem>
                  </Tooltip>
                )}
              </MenuList>
            </Menu>
          </Box>
      </Flex>

      <CardBody px={5} py={4} display="flex" flexDirection="column" flex={1}>
        {/* Title */}
        <Text fontSize="15px" fontWeight="600" color="gray.800" lineHeight="1.4" noOfLines={2} mb={2}>
          {title}
        </Text>

        {/* Period + dimensions + assignee */}
        <HStack spacing={3} mb={3} flexWrap="wrap" rowGap={1}>
          <HStack spacing={1.5}>
            <CalendarBlank size={13} color="#A0AEC0" />
            <Text fontSize="13px" color="gray.500">{task.year}{task.quarter ? ` ${task.quarter}` : ""}</Text>
          </HStack>
          {task.dimensions?.filter((dim) => dim.value !== "All").map((dim, idx) => (
            <HStack key={idx} spacing={1.5}>
              <TreeStructure size={13} color="#A0AEC0" />
              <Text fontSize="13px" color="gray.500">{dim.value}</Text>
            </HStack>
          ))}
          <Tag size="sm" borderRadius="md" bg="#FFF8E1" px={2} py={0.5} gap={1.5}>
            <User size={12} color="#718096" />
            <Text fontSize="11px" fontWeight="600" color="gray.700" textTransform="uppercase">{task.assigneeName}</Text>
          </Tag>
        </HStack>

        {/* Status bar */}
        <Box mt="auto" pt={3} maxW="50%" minW="120px">
          <Flex alignItems="center" gap={1.5} mb={1}>
            <Text fontSize="xs" fontWeight="600" color="gray.600">{statusConfig.label}</Text>
            {task.originalStatus === QUESTIONNAIRE_STATUSES.FINALIZED && (
              <Lock size={12} color="#718096" weight="fill" />
            )}
          </Flex>
          <Box h="10px" borderRadius="full" bg="gray.100" overflow="hidden">
            <Box h="100%" w={`${statusConfig.progress}%`} borderRadius="full" bg={statusConfig.color} />
          </Box>
        </Box>

        {/* Only the latest reminder shows — the list endpoint returns just that one per task. */}
        {reminderLabel && (
          <Flex align="center" gap={1.5} mt={3}>
            <Bell size={13} color={isJustReminded ? "#3F7A0A" : "#718096"} />
            <Text fontSize="xs" fontWeight="600" color={isJustReminded ? "limeShade.700" : "gray.500"}>
              {reminderLabel}
            </Text>
          </Flex>
        )}
      </CardBody>
    </Card>
  );
};

export default TaskCard;
